import {FC, ReactNode, useState, useCallback, useMemo, useRef, useEffect} from 'react'
import {useTranslation} from 'react-i18next'
import {Flexbox} from '@components/ui/flexbox/FlexBox.tsx'
import {Button} from '@components/ui/button/Button.tsx'
import {SelectPopover, SelectTrigger} from '@components/ui/select-atoms/SelectAtoms.tsx'
import {SelectProvider} from '@ariakit/react'
import {StyledDisplayValueWrapper, StyledCountIndicator} from '@components/ui/multi-select/style.ts'
import useWindowDimensions from '@hooks/useWindowDimensions.ts'
import {InputText} from '@components/commons/input-text/InputText.tsx'

type BaseTextInputFilterProps = {
    displayLabel: string
    displayIcon: ReactNode
    placeholder?: string
    sameWidth?: boolean
    numbersOnly?: boolean
    maxLength?: number
}

type SingleValueProps = BaseTextInputFilterProps & {
    multiple?: false
    value: string
    onChange: (value: string) => void
}

type MultipleValueProps = BaseTextInputFilterProps & {
    multiple: true
    value: string[]
    onChange: (value: string[]) => void
}

type TextInputFilterProps = SingleValueProps | MultipleValueProps

const MAX_LENGTH = 5

export const TextInputFilter: FC<TextInputFilterProps> = ({
    value,
    onChange,
    displayLabel,
    displayIcon,
    placeholder,
    sameWidth,
    numbersOnly = true,
    multiple = false,
    maxLength = MAX_LENGTH
}) => {
    const {t} = useTranslation()
    const [inputValue, setInputValue] = useState('')
    const {width} = useWindowDimensions()
    const isMobile = useMemo(() => width < 768, [width])
    const inputRef = useRef<HTMLInputElement>(null)
    const [isOpen, setIsOpen] = useState(false)

    useEffect(() => {
        if (isOpen && !isMobile && inputRef.current) {
            setTimeout(() => {
                inputRef.current?.focus()
            }, 50)
        }
    }, [isOpen, isMobile])

    const handleClose = useCallback(() => {
        setInputValue('')
        setIsOpen(false)
    }, [])

    const handleApply = useCallback(() => {
        const trimmedValue = inputValue.trim()
        if (!trimmedValue || trimmedValue.length > maxLength) return

        if (multiple) {
            const currentValues = value as string[]
            if (!currentValues.includes(trimmedValue)) {
                ;(onChange as (value: string[]) => void)([...currentValues, trimmedValue])
            }
        } else {
            ;(onChange as (value: string) => void)(trimmedValue)
        }
        handleClose()
    }, [inputValue, value, onChange, multiple, maxLength, handleClose])

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === 'Enter') {
                e.preventDefault()
                handleApply()
                return
            }

            if (e.key === 'Backspace' && !inputValue && multiple && Array.isArray(value) && value.length > 0) {
                ;(onChange as (value: string[]) => void)((value as string[]).slice(0, -1))
                return
            }

            if (
                numbersOnly &&
                !/^\d$/.test(e.key) &&
                !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(e.key)
            ) {
                e.preventDefault()
            }
        },
        [inputValue, value, onChange, multiple, numbersOnly, handleApply]
    )

    const handleInputChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const newValue = numbersOnly
                ? e.target.value.replace(/\D/g, '').slice(0, maxLength)
                : e.target.value.slice(0, maxLength)
            setInputValue(newValue)
        },
        [numbersOnly, maxLength]
    )

    const getDisplayCount = () => {
        if (multiple) {
            return Array.isArray(value) && value.length > 0 ? ` (${value.length})` : ''
        }
        return value ? ' (1)' : ''
    }

    return (
        <SelectProvider value={value} setValue={onChange}>
            <SelectTrigger
                displayValue={() => (
                    <StyledDisplayValueWrapper gap={2} align={'center'}>
                        <p>
                            {displayLabel}
                            <StyledCountIndicator className={getDisplayCount() ? 'visible' : ''}>
                                {getDisplayCount()}
                            </StyledCountIndicator>
                        </p>
                        {displayIcon}
                    </StyledDisplayValueWrapper>
                )}
                onClick={() => setIsOpen(prev => !prev)}
            />
            <SelectPopover gutter={8} sameWidth={sameWidth} unmountOnHide onClose={handleClose}>
                <Flexbox direction="column" gap={2}>
                    <InputText
                        type={'text'}
                        ref={inputRef}
                        placeholder={placeholder}
                        value={inputValue}
                        onChange={handleInputChange}
                        onKeyDown={handleKeyDown}
                        maxLength={maxLength}
                        inputMode={numbersOnly ? 'numeric' : 'text'}
                        pattern={numbersOnly ? '\\d*' : undefined}
                    />
                    <Button variant="primaryLight" fullWidth onClick={handleApply}>
                        {t('commons:apply')}
                    </Button>
                </Flexbox>
            </SelectPopover>
        </SelectProvider>
    )
}
